import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import * as api from '../../lib/api';
import { SPACE_TYPE_LABELS } from '../../lib/fit';

const SURFACES = ['concrete', 'asphalt', 'gravel', 'dirt', 'grass'];
const AMP_SERVICES = [15, 20, 30, 50];

const BLANK = {
  prefix: 'A-', suffix: '', start: '1', count: '10', pad: '2',
  space_type: 'open', length_ft: '40', width_ft: '12', height_clearance_ft: '',
  surface: 'gravel', is_pull_through: false, is_corner: false,
  has_power: false, electrical_amps: [], has_water: false, has_sewer: false,
  monthly_rate: '', notes: '',
};

/**
 * Units come in rows, not one at a time — a new yard is fifty numbered spaces
 * with identical dimensions. Typing that form fifty times is how facilities
 * end up half-loaded, so this creates a whole row in one transaction and shows
 * exactly which numbers it will produce before anything is written.
 */
export default function AddUnitsPage() {
  const { facilityId } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(BLANK);
  const [preview, setPreview] = useState({ numbers: [], count: 0 });
  const [previewError, setPreviewError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [conflicts, setConflicts] = useState(null);

  const set = (key) => (event) => {
    const target = event.target;
    setForm((f) => ({ ...f, [key]: target.type === 'checkbox' ? target.checked : target.value }));
  };

  const pattern = useMemo(() => ({
    prefix: form.prefix, suffix: form.suffix,
    start: Number(form.start) || 0, count: Number(form.count) || 0, pad: Number(form.pad) || 0,
  }), [form.prefix, form.suffix, form.start, form.count, form.pad]);

  // The preview comes from the same function that does the creating, so what
  // is shown is what is written — a client-side reimplementation would drift.
  useEffect(() => {
    if (pattern.count < 1) { setPreview({ numbers: [], count: 0 }); setPreviewError(null); return undefined; }
    let live = true;
    const timer = setTimeout(() => {
      api.adminPreviewUnits(pattern)
        .then((payload) => { if (live) { setPreview(payload); setPreviewError(null); } })
        .catch((err) => { if (live) { setPreview({ numbers: [], count: 0 }); setPreviewError(err.message); } });
    }, 250);
    return () => { live = false; clearTimeout(timer); };
  }, [pattern]);

  const toggleAmp = (amp) => setForm((f) => ({
    ...f,
    electrical_amps: f.electrical_amps.includes(amp)
      ? f.electrical_amps.filter((a) => a !== amp)
      : [...f.electrical_amps, amp].sort((a, b) => a - b),
  }));

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true); setError(null); setConflicts(null);
    try {
      await api.adminCreateUnits(facilityId, {
        ...pattern,
        unit: {
          space_type: form.space_type,
          length_ft: Number(form.length_ft),
          width_ft: Number(form.width_ft),
          height_clearance_ft: form.height_clearance_ft === '' ? null : Number(form.height_clearance_ft),
          surface: form.surface,
          is_pull_through: form.is_pull_through,
          is_corner: form.is_corner,
          has_power: form.has_power,
          electrical_amps: form.electrical_amps,
          has_water: form.has_water,
          has_sewer: form.has_sewer,
          monthly_rate: Number(form.monthly_rate),
          notes: form.notes,
        },
      });
      navigate(`/admin/facilities/${facilityId}/units`);
    } catch (err) {
      setError(err.message);
      setConflicts(err.details?.existing ?? null);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
      <Link to={`/admin/facilities/${facilityId}/units`}
            className="mb-3 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-slate-900">
        <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Units
      </Link>
      <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">Add a row of units</h1>
      <p className="mb-6 mt-1 text-sm text-slate-600">
        Every unit in the row shares the dimensions and rate below. Run it again for the next row.
      </p>

      {error && (
        <div role="alert" className="mb-4 rounded-lg bg-rose-50 p-3 text-sm text-rose-800">
          <p>{error}</p>
          {conflicts && (
            <p className="mt-1.5 font-mono text-xs">{conflicts.join(', ')}</p>
          )}
        </div>
      )}

      <form onSubmit={submit} className="space-y-6">
        <section className="rounded-xl border border-slate-200 bg-white p-5">
          <h2 className="mb-3 text-sm font-bold text-slate-900">Numbering</h2>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
            <div>
              <label className="field-label mb-1" htmlFor="prefix">Prefix</label>
              <input id="prefix" maxLength={12} className="field-input" value={form.prefix} onChange={set('prefix')} />
            </div>
            <div>
              <label className="field-label mb-1" htmlFor="start">Start at</label>
              <input id="start" type="number" min={0} className="field-input" value={form.start} onChange={set('start')} />
            </div>
            <div>
              <label className="field-label mb-1" htmlFor="count">How many</label>
              <input id="count" type="number" min={1} max={500} required className="field-input"
                     value={form.count} onChange={set('count')} />
            </div>
            <div>
              <label className="field-label mb-1" htmlFor="pad">Digits</label>
              <input id="pad" type="number" min={0} max={6} className="field-input" value={form.pad} onChange={set('pad')} />
            </div>
            <div>
              <label className="field-label mb-1" htmlFor="suffix">Suffix</label>
              <input id="suffix" maxLength={12} className="field-input" value={form.suffix} onChange={set('suffix')} />
            </div>
          </div>

          <div className="mt-4 rounded-lg bg-slate-50 p-3">
            {previewError ? (
              <p className="text-sm font-semibold text-rose-700">{previewError}</p>
            ) : (
              <>
                <p className="text-xs font-bold uppercase tracking-wide text-slate-500">
                  Creates {preview.count} unit{preview.count === 1 ? '' : 's'}
                </p>
                <p className="mt-1 font-mono text-sm text-slate-800">
                  {preview.count > 8
                    ? `${preview.numbers.slice(0, 7).join(', ')} … ${preview.numbers[preview.count - 1]}`
                    : preview.numbers.join(', ')}
                </p>
              </>
            )}
          </div>
        </section>

        <section className="rounded-xl border border-slate-200 bg-white p-5">
          <h2 className="mb-3 text-sm font-bold text-slate-900">Space</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="field-label mb-1" htmlFor="space_type">Type</label>
              <select id="space_type" className="field-input" value={form.space_type} onChange={set('space_type')}>
                {Object.entries(SPACE_TYPE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="field-label mb-1" htmlFor="surface">Surface</label>
              <select id="surface" className="field-input" value={form.surface} onChange={set('surface')}>
                {SURFACES.map((s) => <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</option>)}
              </select>
            </div>
          </div>

          <div className="mt-3 grid grid-cols-3 gap-3">
            <div>
              <label className="field-label mb-1" htmlFor="length_ft">Length (ft)</label>
              <input id="length_ft" type="number" min={4} max={200} step="0.5" required className="field-input"
                     value={form.length_ft} onChange={set('length_ft')} />
            </div>
            <div>
              <label className="field-label mb-1" htmlFor="width_ft">Width (ft)</label>
              <input id="width_ft" type="number" min={4} max={60} step="0.5" required className="field-input"
                     value={form.width_ft} onChange={set('width_ft')} />
            </div>
            <div>
              <label className="field-label mb-1" htmlFor="height_clearance_ft">Clearance (ft)</label>
              <input id="height_clearance_ft" type="number" min={4} max={40} step="0.5" className="field-input"
                     value={form.height_clearance_ft} onChange={set('height_clearance_ft')} />
              <p className="mt-1 text-xs text-slate-500">Blank means open sky.</p>
            </div>
          </div>

          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            <Check id="is_pull_through" label="Pull-through" checked={form.is_pull_through} onChange={set('is_pull_through')} />
            <Check id="is_corner" label="Corner space" checked={form.is_corner} onChange={set('is_corner')} />
          </div>
        </section>

        <section className="rounded-xl border border-slate-200 bg-white p-5">
          <h2 className="mb-3 text-sm font-bold text-slate-900">Hookups</h2>
          <div className="grid gap-2 sm:grid-cols-3">
            <Check id="has_power" label="Power" checked={form.has_power} onChange={set('has_power')} />
            <Check id="has_water" label="Water" checked={form.has_water} onChange={set('has_water')} />
            <Check id="has_sewer" label="Sewer" checked={form.has_sewer} onChange={set('has_sewer')} />
          </div>
          {form.has_power && (
            <div className="mt-3">
              <p className="field-label mb-1.5">Amp service</p>
              <div className="flex flex-wrap gap-2">
                {AMP_SERVICES.map((amp) => (
                  <button key={amp} type="button" onClick={() => toggleAmp(amp)}
                          aria-pressed={form.electrical_amps.includes(amp)}
                          className={`rounded-full border px-3 py-1.5 text-sm font-semibold ${
                            form.electrical_amps.includes(amp)
                              ? 'border-brand-600 bg-brand-50 text-brand-800'
                              : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`}>
                    {amp}A
                  </button>
                ))}
              </div>
            </div>
          )}
        </section>

        <section className="rounded-xl border border-slate-200 bg-white p-5">
          <h2 className="mb-3 text-sm font-bold text-slate-900">Rate</h2>
          <div className="sm:max-w-xs">
            <label className="field-label mb-1" htmlFor="monthly_rate">Monthly rate</label>
            <div className="relative">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-500">$</span>
              <input id="monthly_rate" type="number" min={0} step="0.01" required className="field-input pl-7"
                     value={form.monthly_rate} onChange={set('monthly_rate')} />
            </div>
          </div>
          <label className="field-label mb-1 mt-3" htmlFor="notes">Internal notes</label>
          <textarea id="notes" rows={2} maxLength={2000} className="field-input" value={form.notes} onChange={set('notes')} />
        </section>

        <button type="submit" disabled={busy || preview.count < 1}
                className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-brand-700 disabled:opacity-60">
          {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
          Create {preview.count} unit{preview.count === 1 ? '' : 's'}
        </button>
      </form>
    </div>
  );
}

function Check({ id, label, checked, onChange }) {
  return (
    <label htmlFor={id} className="flex items-center gap-2 text-sm text-slate-800">
      <input id={id} type="checkbox" className="h-4 w-4 rounded border-slate-300 text-brand-600"
             checked={checked} onChange={onChange} />
      {label}
    </label>
  );
}
